
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, Share2, Info, Play, Pause, Volume2, VolumeX, MapPin, Calendar, RefreshCcw } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useInView } from 'react-intersection-observer';
import { cn } from '@/lib/utils';
import { getAddressFromCoordinates } from '@/services/databaseService';
import { getLikesCount, hasUserLikedVideo, toggleLike } from '@/services/likesService';
import LikesModal from '@/components/LikesModal';

interface VideoWallProps {
  videos: any[];
  onLike?: (videoId: string | number) => void;
  onShare?: (videoId: string | number) => void;
  onComment?: (videoId: string | number) => void;
  onVideoEnded?: () => void;
  autoPlay?: boolean;
}

const VideoWall: React.FC<VideoWallProps> = ({ 
  videos = [], 
  onLike, 
  onShare, 
  onComment, 
  onVideoEnded,
  autoPlay = true
}) => {
  const navigate = useNavigate();
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(true); // Start muted by default
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoDurations, setVideoDurations] = useState<{[key: string]: number}>({});
  const [videoRefs, setVideoRefs] = useState<{[key: string]: HTMLVideoElement | null}>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fullscreenVideoRef = useRef<HTMLVideoElement | null>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const [videoErrors, setVideoErrors] = useState<{[key: string]: boolean}>({});
  const [videoLoading, setVideoLoading] = useState<{[key: string]: boolean}>({});
  const [videoLocations, setVideoLocations] = useState<{[key: string]: string}>({});
  const [initialized, setInitialized] = useState(false);
  const [videoSources, setVideoSources] = useState<{[key: string]: string}>({});
  const [userNames, setUserNames] = useState<{[key: string]: string}>({});
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [likeCounts, setLikeCounts] = useState<{[key: string]: number}>({});
  const [likedByUser, setLikedByUser] = useState<{[key: string]: boolean}>({});
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [selectedVideoForLikes, setSelectedVideoForLikes] = useState<string | number | null>(null);
  
  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        setCurrentUser(data.session.user);
      }
    };
    
    checkAuth();
  }, []);

  // Function to get the proper video source URL - resolving the previous error
  const getVideoSource = (video: any) => {
    if (!video || !video.id) return '';
    
    // Check if we have a processed video source
    if (videoSources[video.id]) {
      return videoSources[video.id];
    }
    
    // Fallback to any available source format
    if (video.videoUrl && typeof video.videoUrl === 'string') {
      return video.videoUrl;
    }
    
    if (video.video_path) {
      if (typeof video.video_path === 'string') {
        return video.video_path;
      } else if (typeof video.video_path === 'object' && video.video_path !== null) {
        return String(video.video_path);
      }
    }
    
    return '';
  };

  // Function to open fullscreen video
  const openFullscreenVideo = (video: any) => {
    if (videoErrors[video.id]) {
      // Don't open fullscreen for videos with errors
      return;
    }
    
    setIsFullscreen(true);
    setCurrentVideoIndex(videos.findIndex(v => v.id === video.id));
    
    // Pause all videos when opening fullscreen
    Object.values(videoRefs).forEach(video => {
      if (video) video.pause();
    });
  };

  // Function to close fullscreen
  const closeFullscreen = () => {
    setIsFullscreen(false);
    
    if (fullscreenVideoRef.current) {
      fullscreenVideoRef.current.pause();
    }
    
    // Resume playing the current video
    setTimeout(() => {
      const currentVideo = videos[currentVideoIndex];
      if (currentVideo && isPlaying && !videoErrors[currentVideo.id]) {
        const videoElement = videoRefs[currentVideo.id];
        if (videoElement) {
          videoElement.play().catch(err => {
            console.error("Error resuming video after fullscreen:", err);
          });
        }
      }
    }, 200);
  };

  // Load likes data for videos
  useEffect(() => {
    const loadLikesData = async () => {
      if (!videos.length) return;
      
      const counts: {[key: string]: number} = {};
      const liked: {[key: string]: boolean} = {};
      
      for (const video of videos) {
        if (!video.id) continue;
        
        // Get likes count for this video
        const videoId = typeof video.id === 'string' ? parseInt(video.id, 10) : video.id;
        if (!isNaN(videoId)) {
          const count = await getLikesCount(videoId);
          counts[video.id] = count;
          
          // Check if current user liked this video
          if (currentUser) {
            const hasLiked = await hasUserLikedVideo(currentUser.id, videoId);
            liked[video.id] = hasLiked;
          }
        }
      }
      
      setLikeCounts(counts);
      setLikedByUser(liked);
    };
    
    loadLikesData();
  }, [videos, currentUser]);

  // Load formatted location for each video
  useEffect(() => {
    const loadLocations = async () => {
      const newLocations: {[key: string]: string} = {};
      
      for (const video of videos) {
        if (video.location) {
          let locationData = video.location;
          
          // Parse location if it's a string
          if (typeof locationData === 'string') {
            try {
              locationData = JSON.parse(locationData);
            } catch (e) {
              newLocations[video.id] = locationData;
              continue;
            }
          }
          
          // If we have coordinates, get the address
          if (locationData && locationData.latitude && locationData.longitude) {
            try {
              const address = await getAddressFromCoordinates(
                locationData.latitude, 
                locationData.longitude
              );
              newLocations[video.id] = address;
            } catch (e) {
              console.error('Error getting address:', e);
              if (locationData.address) {
                newLocations[video.id] = locationData.address;
              }
            }
          } else if (locationData && locationData.address) {
            newLocations[video.id] = locationData.address;
          }
        }
      }
      
      setVideoLocations(newLocations);
    };
    
    if (videos.length > 0) {
      loadLocations();
    }
  }, [videos]);

  // Fetch user profile names
  useEffect(() => {
    const fetchUserProfiles = async () => {
      const newUserNames: {[key: string]: string} = {};
      const uniqueUserIds = [...new Set(videos.filter(v => v.user_id).map(v => v.user_id))];
      
      if (uniqueUserIds.length === 0) return;
      
      try {
        const { data: profiles, error } = await supabase
          .from('profile')
          .select('user_id, name')
          .in('user_id', uniqueUserIds);
          
        if (error) {
          console.error("Error fetching user profiles:", error);
          return;
        }
        
        if (profiles && profiles.length > 0) {
          profiles.forEach(profile => {
            if (!profile.user_id) return;
            
            let displayName = "User";
            if (profile.name) {
              if (typeof profile.name === 'string' && 
                  (profile.name.startsWith('{') || profile.name.startsWith('['))) {
                try {
                  const parsedName = JSON.parse(profile.name);
                  displayName = typeof parsedName === 'string' ? parsedName : String(parsedName);
                } catch (e) {
                  displayName = profile.name;
                }
              } else if (typeof profile.name === 'object') {
                displayName = JSON.stringify(profile.name);
              } else {
                displayName = String(profile.name);
              }
            }
            
            newUserNames[profile.user_id] = displayName;
          });
          
          setUserNames(newUserNames);
        }
      } catch (err) {
        console.error("Error processing user profiles:", err);
      }
    };
    
    fetchUserProfiles();
  }, [videos]);

  // Process video sources
  useEffect(() => {
    const processVideoSources = async () => {
      const sources: {[key: string]: string} = {};
      
      for (const video of videos) {
        if (!video.id) continue;
        
        let videoUrl = '';
        
        if (video.videoUrl && typeof video.videoUrl === 'string') {
          videoUrl = video.videoUrl;
        } else if (video.video_path) {
          if (typeof video.video_path === 'string') {
            videoUrl = video.video_path;
          } else if (typeof video.video_path === 'object' && video.video_path !== null) {
            videoUrl = String(video.video_path);
          }
        }
        
        if (videoUrl.startsWith('videos/') || videoUrl.startsWith('/videos/')) {
          try {
            const { data } = await supabase.storage
              .from('recordings')
              .getPublicUrl(videoUrl.replace(/^\//, ''));
            
            if (data && data.publicUrl) {
              videoUrl = data.publicUrl;
            }
          } catch (e) {
            console.error(`Error getting public URL for ${videoUrl}:`, e);
          }
        }
        
        if (videoUrl) {
          sources[video.id] = videoUrl;
        }
      }
      
      setVideoSources(sources);
    };
    
    processVideoSources();
  }, [videos]);

  // Initialize videos
  useEffect(() => {
    const initializeVideos = async () => {
      if (initialized) return;
      
      Object.entries(videoRefs).forEach(([id, videoElement]) => {
        if (videoElement) {
          videoElement.muted = true;
          setVideoLoading(prev => ({ ...prev, [id]: true }));
          
          videoElement.play()
            .then(() => {
              videoElement.muted = true;
            })
            .catch(err => {
              console.warn(`Video ${id} initialization warning (expected)`, err);
            })
            .finally(() => {
              setVideoLoading(prev => ({ ...prev, [id]: false }));
            });
        }
      });
      
      setInitialized(true);
    };
    
    if (Object.keys(videoRefs).length > 0 && !initialized) {
      initializeVideos();
    }
  }, [videoRefs, initialized]);

  // Setup intersection observer
  useEffect(() => {
    if (!containerRef.current) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const videoId = entry.target.getAttribute('data-id');
          if (!videoId) return;

          const videoElement = videoRefs[videoId];
          if (!videoElement) return;

          if (entry.isIntersecting) {
            const index = videos.findIndex(v => v.id.toString() === videoId.toString());
            if (index !== -1) {
              setCurrentVideoIndex(index);
              
              if (isPlaying && !isScrolling && !isFullscreen && !videoErrors[videoId]) {
                videoElement.muted = true;
                videoElement.play()
                  .catch(err => {
                    console.error("Error playing video:", err);
                    setVideoErrors(prev => ({ ...prev, [videoId]: true }));
                    toast.error(`Error playing video: ${err.message || 'Unknown error'}`);
                  });
              }
            }
          } else {
            videoElement.pause();
          }
        });
      },
      {
        root: containerRef.current,
        threshold: 0.6,
      }
    );

    observerRef.current = observer;

    Object.keys(videoRefs).forEach(id => {
      const videoElement = videoRefs[id];
      if (videoElement) {
        observer.observe(videoElement);
      }
    });

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [videoRefs, videos, isPlaying, isScrolling, isFullscreen, videoErrors]);

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true);
      
      Object.values(videoRefs).forEach(video => {
        if (video) video.pause();
      });
      
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
        
        const currentVideo = videos[currentVideoIndex];
        if (currentVideo && isPlaying && !isFullscreen && !videoErrors[currentVideo.id]) {
          const videoElement = videoRefs[currentVideo.id];
          if (videoElement) {
            videoElement.muted = true;
            videoElement.play()
              .catch(err => {
                console.error("Error playing video after scroll:", err);
                setVideoErrors(prev => ({ ...prev, [currentVideo.id]: true }));
              });
          }
        }
      }, 150);
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [videoRefs, currentVideoIndex, videos, isPlaying, isFullscreen, videoErrors]);

  // Set video ref for a specific video
  const setVideoRef = useCallback((element: HTMLVideoElement | null) => {
    if (element) {
      const videoId = element.getAttribute('data-id');
      if (videoId) {
        setVideoRefs(prev => ({
          ...prev,
          [videoId]: element
        }));
      }
    }
  }, []);

  // Handle video duration loaded
  const handleVideoDuration = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    const videoId = video.getAttribute('data-id');
    if (videoId) {
      setVideoDurations(prev => ({
        ...prev,
        [videoId]: video.duration
      }));
    }
  };

  // Handle play button click
  const togglePlay = () => {
    const newIsPlaying = !isPlaying;
    setIsPlaying(newIsPlaying);
    
    const currentVideo = videos[currentVideoIndex];
    if (currentVideo) {
      const videoElement = isFullscreen 
        ? fullscreenVideoRef.current 
        : videoRefs[currentVideo.id];
        
      if (videoElement) {
        if (newIsPlaying) {
          videoElement.play()
            .catch(err => {
              console.error("Error playing video:", err);
              setIsPlaying(false);
              setVideoErrors(prev => ({ ...prev, [currentVideo.id]: true }));
              toast.error(`Error playing video: ${err.message || 'Unknown error'}`);
            });
        } else {
          videoElement.pause();
        }
      }
    }
  };

  // Handle mute button click
  const toggleMute = () => {
    const newIsMuted = !isMuted;
    setIsMuted(newIsMuted);
    
    if (isFullscreen && fullscreenVideoRef.current) {
      fullscreenVideoRef.current.muted = newIsMuted;
      return;
    }
    
    Object.values(videoRefs).forEach(video => {
      if (video) {
        video.muted = newIsMuted;
      }
    });
  };

  // Handle video play event
  const handlePlay = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const videoElement = e.currentTarget;
    
    const videoId = videoElement.getAttribute('data-id');
    if (videoId) {
      setVideoLoading(prev => ({ ...prev, [videoId]: false }));
      videoElement.muted = true;
    }
    
    Object.entries(videoRefs).forEach(([id, video]) => {
      if (id !== videoId && video) {
        video.pause();
      }
    });
  };

  // Handle video error
  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement>, videoId: string) => {
    console.error(`Video error for video ID ${videoId}:`, e);
    setVideoErrors(prev => ({ ...prev, [videoId]: true }));
    
    const videoSrc = e.currentTarget.src;
    console.error(`Failed video source: "${videoSrc}"`);
    
    if (!videoSrc) {
      console.error(`Video ${videoId} has empty source!`);
    }
    
    toast.error("Error playing video. Please try again later.");
  };

  // Attempt to reload video on error
  const handleRetryVideo = (videoId: string | number) => {
    const videoElement = videoRefs[videoId];
    if (videoElement) {
      setVideoLoading(prev => ({ ...prev, [videoId]: true }));
      
      const sourceUrl = videoSources[videoId.toString()];
      if (!sourceUrl) {
        toast.error("Could not find video source to retry");
        setVideoLoading(prev => ({ ...prev, [videoId]: false }));
        return;
      }
      
      videoElement.src = '';
      videoElement.load();
      
      setTimeout(() => {
        if (videoElement) {
          videoElement.src = sourceUrl;
          videoElement.load();
          videoElement.muted = true;
          
          videoElement.play()
            .then(() => {
              setVideoErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[videoId.toString()];
                return newErrors;
              });
            })
            .catch(err => {
              console.error("Error retrying video:", err);
              toast.error(`Retry failed: ${err.message || 'Unknown error'}`);
            })
            .finally(() => {
              setVideoLoading(prev => ({ ...prev, [videoId]: false }));
            });
        }
      }, 300);
    }
  };

  // Handle like button click
  const handleLike = async (e: React.MouseEvent, videoId: string | number) => {
    e.stopPropagation(); // Prevent video opening in fullscreen
    
    if (!currentUser) {
      toast.error("Please sign in to like videos");
      return;
    }
    
    const numericVideoId = typeof videoId === 'string' ? parseInt(videoId, 10) : videoId;
    
    if (isNaN(numericVideoId)) {
      console.error("Invalid video ID:", videoId);
      return;
    }
    
    // Toggle the like status
    const isNowLiked = await toggleLike(currentUser.id, numericVideoId);
    
    // Update UI
    setLikedByUser(prev => ({
      ...prev,
      [videoId]: isNowLiked
    }));
    
    setLikeCounts(prev => ({
      ...prev,
      [videoId]: Math.max(0, (prev[videoId] || 0) + (isNowLiked ? 1 : -1))
    }));
    
    // If custom like handler provided, call it
    if (onLike) {
      onLike(videoId);
    }
  };

  // Handle share button click
  const handleShare = (e: React.MouseEvent, videoId: string | number) => {
    e.stopPropagation(); // Prevent video opening in fullscreen
    
    // If custom share handler provided, call it
    if (onShare) {
      onShare(videoId);
    } else {
      navigator.clipboard.writeText(window.location.origin + '/video/' + videoId)
        .then(() => {
          toast.success("Video link copied to clipboard!");
        })
        .catch(err => {
          console.error("Error copying link:", err);
          toast.error("Failed to copy link");
        });
    }
  };

  // Handle video info button click
  const handleVideoInfo = (video: any) => {
    navigate(`/video/${video.id}`);
  };

  // Handle show likes modal
  const handleShowLikes = (e: React.MouseEvent, videoId: string | number) => {
    e.stopPropagation(); // Prevent video opening in fullscreen
    setSelectedVideoForLikes(videoId);
    setShowLikesModal(true);
  };

  // Clean any invalid text in emotion name
  const cleanEmotionText = (text: string) => {
    if (!text) return "Unknown";
    return text.replace(/Infinity:?\s*NaN/i, "").trim() || "Unknown";
  };

  // Format duration from seconds to MM:SS
  const formatDuration = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (videos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] text-center p-4">
        <p className="text-lg mb-4">No videos available</p>
        <Button onClick={() => navigate('/record')}>Record a Video</Button>
      </div>
    );
  }

  return (
    <>
<div 
        ref={containerRef}
        className="w-full h-screen overflow-y-auto snap-y snap-mandatory overflow-x-hidden"
        style={{ marginTop: '0', paddingTop: '0' }}
      >
        {videos.map((video, index) => {
          const videoSrc = getVideoSource(video);
          const displayUserName = userNames[video.user_id] || video.userName || "User";
          
          return (
            <div 
              key={video.id} 
              className="w-full h-screen snap-start relative"
            >
              <div className="absolute inset-0 bg-black">
                {videoErrors[video.id] ? (
                  <div className="flex flex-col items-center justify-center h-full bg-black text-white p-4">
                    <div className="mb-4 text-red-500">Error playing video</div>
                    <div className="text-xs mb-2 text-gray-400 max-w-xs text-center">
                      Source: {videoSrc ? videoSrc.substring(0, 100) + '...' : 'No source available'}
                    </div>
                    <Button 
                      onClick={() => handleRetryVideo(video.id)}
                      variant="outline"
                      className="text-white border-white hover:bg-white/10"
                    >
                      <RefreshCcw className="mr-2 h-4 w-4" />
                      Retry
                    </Button>
                  </div>
                ) : (
                  <>
                    {videoLoading[video.id] && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
                      </div>
                    )}
                    
                    {videoSrc ? (
                      <video
                        ref={setVideoRef}
                        src={videoSrc}
                        className="absolute inset-0 w-full h-full object-cover"
                        loop
                        playsInline
                        controls={false}
                        data-id={video.id}
                        preload="auto"
                        muted={true}
                        onLoadedMetadata={handleVideoDuration}
                        onPlay={handlePlay}
                        onError={(e) => handleVideoError(e, video.id.toString())}
                        onLoadStart={() => setVideoLoading(prev => ({ ...prev, [video.id]: true }))}
                        onLoadedData={() => setVideoLoading(prev => ({ ...prev, [video.id]: false }))}
                        onClick={() => openFullscreenVideo(video)}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full bg-black text-white p-4">
                        <div className="mb-4 text-red-500">Video source unavailable</div>
                        <Button 
                          onClick={() => handleRetryVideo(video.id)}
                          variant="outline"
                          className="text-white border-white hover:bg-white/10"
                        >
                          <RefreshCcw className="mr-2 h-4 w-4" />
                          Try Again
                        </Button>
                      </div>
                    )}
                  </>
                )}
                
                <div className="absolute inset-0 flex items-center justify-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-16 w-16 rounded-full bg-black/30 text-white transition-opacity",
                      isPlaying ? "opacity-0 hover:opacity-100" : "opacity-100"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlay();
                    }}
                  >
                    {isPlaying ? (
                      <Pause className="h-8 w-8" />
                    ) : (
                      <Play className="h-8 w-8" />
                    )}
                  </Button>
                </div>
                
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <div className="flex items-end justify-between">
                    <div className="flex-1 pr-4">
                      <div className="flex items-center mb-2">
                        <Avatar className="h-12 w-12 mr-2 border-2 border-yellow-400">
                          <AvatarImage src={video.userAvatar || video.avatar_path} />
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {displayUserName?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-base font-medium text-white">{displayUserName}</span>
                        
                        <div className="flex items-center ml-2 space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-full bg-black/30 text-white h-8 w-8 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleMute();
                            }}
                          >
                            {isMuted ? (
                              <VolumeX className="h-4 w-4" />
                            ) : (
                              <Volume2 className="h-4 w-4" />
                            )}
                          </Button>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-full bg-black/30 text-white h-8 w-8 p-0"
                            onClick={(e) => handleShare(e, video.id)}
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-full bg-black/30 text-white h-8 w-8 p-0 flex flex-col items-center"
                            onClick={(e) => handleLike(e, video.id)}
                          >
                            <Heart 
                              className={`h-4 w-4 ${likedByUser[video.id] ? 'fill-red-500 text-red-500' : ''}`} 
                            />
                          </Button>
                          
                          {likeCounts[video.id] > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs text-white bg-black/30 px-2 h-6"
                              onClick={(e) => handleShowLikes(e, video.id)}
                            >
                              {likeCounts[video.id]} {likeCounts[video.id] === 1 ? 'like' : 'likes'}
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      <h3 className="text-lg font-bold text-white mb-1">{video.title || 'Video'}</h3>
                      
                      <div className="flex items-center text-xs text-gray-300">
                        <span>{formatDuration(videoDurations[video.id] || 0)}</span>
                        {video.dominantEmotion && (
                          <>
                            <span className="mx-1">•</span>
                            <span className="capitalize">Dominant emotion: {cleanEmotionText(video.dominantEmotion || "")}</span>
                          </>
                        )}
                      </div>
                      
                      {(videoLocations[video.id] || video.location) && (
                        <div className="flex items-center text-xs text-gray-300 mt-1">
                          <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate max-w-[200px]">
                            {videoLocations[video.id] || 
                             (typeof video.location === 'string' ? video.location : 
                             (video.location?.address || 'Location unavailable'))}
                          </span>
                        </div>
                      )}
                      
                      {video.created_at && (
                        <div className="flex items-center text-xs text-gray-300 mt-1">
                          <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span>{new Date(video.created_at).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-center space-y-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full bg-black/30 text-white h-9 w-9 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVideoInfo(video);
                        }}
                      >
                        <Info className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {isFullscreen && currentVideoIndex < videos.length && (
        <div 
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          ref={fullscreenContainerRef}
        >
          <div className="relative w-full h-full">
            {videoErrors[videos[currentVideoIndex].id] ? (
              <div className="flex flex-col items-center justify-center h-full bg-black text-white p-4">
                <div className="mb-4 text-red-500">Error playing video</div>
                <Button 
                  onClick={() => handleRetryVideo(videos[currentVideoIndex].id)}
                  variant="outline"
                  className="text-white border-white hover:bg-white/10 mb-4"
                >
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
                <Button 
                  variant="outline"
                  onClick={closeFullscreen}
                  className="text-white"
                >
                  Close
                </Button>
              </div>
            ) : (
              <>
                {videoLoading[videos[currentVideoIndex].id] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
                  </div>
                )}
                
                <video
                  ref={fullscreenVideoRef}
                  src={getVideoSource(videos[currentVideoIndex])}
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  playsInline
                  loop
                  muted={isMuted} 
                  onLoadStart={() => setVideoLoading(prev => ({ ...prev, [videos[currentVideoIndex].id]: true }))}
                  onLoadedData={() => setVideoLoading(prev => ({ ...prev, [videos[currentVideoIndex].id]: false }))}
                  onError={(e) => {
                    console.error("Fullscreen video error:", e);
                    setVideoErrors(prev => ({ ...prev, [videos[currentVideoIndex].id]: true }));
                  }}
                />
              </>
            )}
            
            <div className="absolute top-4 right-4 flex space-x-2">
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-black/30 text-white"
                onClick={toggleMute}
              >
                {isMuted ? (
                  <VolumeX className="h-5 w-5" />
                ) : (
                  <Volume2 className="h-5 w-5" />
                )}
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-black/30 text-white"
                onClick={closeFullscreen}
              >
                <Info className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <div className="flex items-end justify-between">
                <div className="flex-1 pr-4">
                  <div className="flex items-center mb-2">
                    <Avatar className="h-12 w-12 mr-2 border-2 border-yellow-400">
                      <AvatarImage src={videos[currentVideoIndex].userAvatar || videos[currentVideoIndex].avatar_path} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {(userNames[videos[currentVideoIndex].user_id] || 
                          videos[currentVideoIndex].userName || 'User')?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-base font-medium text-white">
                      {userNames[videos[currentVideoIndex].user_id] || 
                       videos[currentVideoIndex].userName || 'User'}
                    </span>
                    
                    <div className="flex items-center ml-2 space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full bg-black/30 text-white h-8 w-8 p-0" 
                        onClick={(e) => handleShare(e, videos[currentVideoIndex].id)}
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full bg-black/30 text-white h-8 w-8 p-0"
                        onClick={(e) => handleLike(e, videos[currentVideoIndex].id)}
                      >
                        <Heart 
                          className={`h-4 w-4 ${likedByUser[videos[currentVideoIndex].id] ? 'fill-red-500 text-red-500' : ''}`} 
                        />
                      </Button>
                      
                      {likeCounts[videos[currentVideoIndex].id] > 0 && (
                        <span className="text-xs text-white bg-black/30 px-2 py-1 rounded">
                          {likeCounts[videos[currentVideoIndex].id]} {likeCounts[videos[currentVideoIndex].id] === 1 ? 'like' : 'likes'}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-bold text-white mb-1">{videos[currentVideoIndex].title || 'Video'}</h3>
                  
                  <div className="flex items-center text-xs text-gray-300">
                    <span>{formatDuration(videoDurations[videos[currentVideoIndex].id] || 0)}</span>
                    {videos[currentVideoIndex].dominantEmotion && (
                      <>
                        <span className="mx-1">•</span>
                        <span className="capitalize">Dominant emotion: {cleanEmotionText(videos[currentVideoIndex].dominantEmotion || "")}</span>
                      </>
                    )}
                  </div>
                  
                  <div className="flex items-center text-xs text-gray-300 mt-1">
                    <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                    <span className="truncate max-w-[200px]">
                      {videoLocations[videos[currentVideoIndex].id] || 
                       (typeof videos[currentVideoIndex].location === 'string' ? videos[currentVideoIndex].location : 
                       (videos[currentVideoIndex].location?.address || 'Location unavailable'))}
                    </span>
                  </div>
                  
                  {videos[currentVideoIndex].created_at && (
                    <div className="flex items-center text-xs text-gray-300 mt-1">
                      <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
                      <span>{new Date(videos[currentVideoIndex].created_at).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={closeFullscreen}
                  className="text-white bg-black/30 h-9"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLikesModal && selectedVideoForLikes && (
        <LikesModal
          videoId={selectedVideoForLikes}
          isOpen={showLikesModal}
          onClose={() => setShowLikesModal(false)}
        />
      )}
    </>
  );
};

export default VideoWall;
